import React, { useEffect, useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { X, LucideIcon } from "lucide-react";
import {
  SortingState,
  ColumnFiltersState,
  OnChangeFn,
} from "@tanstack/react-table";
import DashboardTableContainer, {
  CustomColumnDef,
} from "Common/Tables/ObjectFiltersTable/DashboardTableContainer";
import LoaderErrorWrapper from "components/LoaderErrorWrapper/LoaderErrorWrapper";
import "./DashboardSlidePanel.css";

interface DashboardSlidePanelProps<T> {
  isOpen: boolean;
  onClose: () => void;
  icon: LucideIcon;
  title: string;
  subtitle: string;
  loading: boolean;
  columns: CustomColumnDef<T>[];
  data: T[];
  totalRecords: number;
  pageIndex: number;
  pageSize: number;
  onPageChange: (pageIndex: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  columnFilters: ColumnFiltersState;
  setColumnFilters: React.Dispatch<React.SetStateAction<ColumnFiltersState>>;
  colValue: any[];
  setFilterValue: (value: any[]) => void;
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
  searchPlaceholder: string;
}

export function DashboardSlidePanel<T>({
  isOpen,
  onClose,
  icon: Icon,
  title,
  subtitle,
  loading,
  columns,
  data,
  totalRecords,
  pageIndex,
  pageSize,
  onPageChange,
  onPageSizeChange,
  columnFilters,
  setColumnFilters,
  colValue,
  setFilterValue,
  sorting,
  onSortingChange,
  searchPlaceholder,
}: DashboardSlidePanelProps<T>) {
  // Disable body scroll when panel is open
  // Use useLayoutEffect to run synchronously before browser paint to prevent visual jump
  useLayoutEffect(() => {
    if (isOpen) {
      // Store original values
      const originalBodyOverflow = document.body.style.overflow;
      const originalHtmlOverflow = document.documentElement.style.overflow;
      
      // Calculate scrollbar width to prevent layout shift
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      const originalBodyPaddingRight = document.body.style.paddingRight;
      
      // Simply prevent scrolling with overflow: hidden
      // This approach doesn't change position, so no visual jump occurs
      document.body.style.overflow = "hidden";
      document.body.style.paddingRight = scrollbarWidth > 0 ? `${scrollbarWidth}px` : "";
      document.documentElement.style.overflow = "hidden";
      
      // Prevent scroll events as a backup
      const preventScroll = (e: WheelEvent | TouchEvent) => {
        // Only prevent if scrolling the body/document
        const target = e.target as HTMLElement;
        if (target === document.body || target === document.documentElement || 
            !target.closest('.dashboard-slide-panel-content')) {
          e.preventDefault();
        }
      };
      
      window.addEventListener("wheel", preventScroll, { passive: false });
      window.addEventListener("touchmove", preventScroll, { passive: false });

      // Cleanup function to restore scrolling
      return () => {
        // Remove event listeners
        window.removeEventListener("wheel", preventScroll);
        window.removeEventListener("touchmove", preventScroll);
        
        // Restore original styles
        document.body.style.overflow = originalBodyOverflow;
        document.body.style.paddingRight = originalBodyPaddingRight;
        document.documentElement.style.overflow = originalHtmlOverflow;
      };
    }
  }, [isOpen]);

  // Track previous isOpen state to detect when panel closes
  const prevIsOpenRef = useRef(isOpen);

  // Reset filters, pagination, and sorting when panel closes
  useEffect(() => {
    // Only reset if panel was open and is now closed
    if (prevIsOpenRef.current && !isOpen) {
      // Reset pagination
      onPageChange(0);
      // Reset filters
      setColumnFilters([]);
      setFilterValue([]);
      // Reset sorting
      onSortingChange([]);
    }
    // Update the ref for next render
    prevIsOpenRef.current = isOpen;
  }, [isOpen, onPageChange, setColumnFilters, setFilterValue, onSortingChange]);

  if (!isOpen) return null;

  const panelContent = (
    <>
      {/* Backdrop */}
      <div
        className="dashboard-slide-panel-backdrop"
        onClick={onClose}
      />

      {/* Slide Panel */}
      <div className="dashboard-slide-panel">
        {/* Panel Header */}
        <div className="dashboard-slide-panel-header">
          {loading ? (
            <div className="dashboard-slide-panel-header-content">
              <div className="d-flex align-items-center justify-content-center py-3">
                <div
                  className="spinner-border spinner-border-sm"
                  role="status"
                  style={{ color: 'white' }}
                >
                  <span className="visually-hidden">Loading...</span>
                </div>
                <span className="ms-2" style={{ color: 'white' }}>Loading...</span>
              </div>
            </div>
          ) : (
            <div className="dashboard-slide-panel-header-content">
              <Icon className="dashboard-slide-panel-header-icon" />
              <div>
                <h2 className="dashboard-slide-panel-title">{title}</h2>
                <p className="dashboard-slide-panel-subtitle">{subtitle}</p>
              </div>
            </div>
          )}
          <button
            onClick={onClose}
            className="dashboard-slide-panel-close"
          >
            <X className="dashboard-slide-panel-close-icon" />
          </button>
        </div>

        {/* Panel Content */}
        <div className="dashboard-slide-panel-content">
          <div className="dashboard-slide-panel-table-wrapper">
            <LoaderErrorWrapper loading={loading} error={null}>
              <DashboardTableContainer
                columns={columns}
                data={data}
                totalRecords={totalRecords}
                pageIndex={pageIndex}
                pageSize={pageSize}
                onPageChange={onPageChange}
                onPageSizeChange={onPageSizeChange}
                columnFilters={columnFilters}
                setColumnFilters={setColumnFilters}
                colValue={setFilterValue}
                sorting={sorting}
                onSortingChange={onSortingChange}
                isBordered={false}
                isPagination={true}
                isGlobalFilter={true}
                SearchPlaceholder={searchPlaceholder}
                manualSorting={true}
              />
            </LoaderErrorWrapper>
          </div>
        </div>
      </div>
    </>
  );

  // Render to document body to avoid positioning issues
  return createPortal(panelContent, document.body);
}

